import { FC, Fragment, useState } from "react";
import dayjs from "dayjs";
import theme from "theme";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppSelector } from "app/hooks/useStore";
// Models
import { RootState } from "app/store";
// Selectors
import { selectCalendarEventByIds } from "app/store/CalendarEvents/CalendarEventsSelectors";
import { selectCurrentAccount } from "app/store/Accounts/Accounts.selectors";
// MUI
import { Box, Popover, Stack, Typography } from "@mui/material";
// Components
import EventPopover from "./EventPopover";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  eventsIds: number[];
  selectedDate: Date | null;
  openDayView: (dateStr: string) => void;
}

const DayEventsPopover:FC<Props> = ({ anchorEl, onClose, eventsIds, selectedDate, openDayView }) => {
  const { t } = useTranslation();

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [eventAnchorEl, setEventAnchorEl] = useState<HTMLElement | null>(null);

  const handleEventClick = (event:any, eventId:number) => {
    event.stopPropagation();
    setEventAnchorEl(event.target);
    setSelectedEventId(eventId);
  }

  const onClosePopover = () => {
    setEventAnchorEl(null);
    setSelectedEventId(null);
  }

  const currentAccount = useAppSelector(selectCurrentAccount);
  const events = useAppSelector((state:RootState) => selectCalendarEventByIds(state, { eventIds: eventsIds }));

  const handleOpenDayView = () => {
    openDayView(dayjs(selectedDate).format('YYYY-MM-DD'));
  }

  const timeFormat = currentAccount?.settings?.timeFormat || 'hh:mm a';
  const open = Boolean(anchorEl);

  if (!open || !selectedDate) return null;
  return (
    <Fragment>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{
          maxWidth: 600,
          p: 2
        }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, cursor: 'pointer', textAlign: 'center', transition: 'color 0.3s', '&:hover': { color: theme.palette.primary.main } }}
            onClick={handleOpenDayView}
          >
            {dayjs(selectedDate).format('DD MMM YYYY')}
          </Typography>

          {events.length > 0 ? (
            <Stack spacing={1}>
              {events.map((event) => (
                <Box key={event.id} onClick={(e:any) => handleEventClick(e, event.id)} sx={{
                  p: 0.5,
                  cursor: 'pointer',
                  bgcolor: event.allDay ? event.type?.color || '#039be5' : 'transparent',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: event.allDay ? event.type?.color || '#039be5' : 'rgba(0, 0, 0, 0.05)',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {!event.allDay ? (
                      <Box sx={{ width: 10, height: 10, bgcolor: event.type?.color || '#039be5', borderRadius: '50%' }} />
                    ) : null}
                    <Typography sx={{ fontSize: '11px', lineHeight: 1 }}>
                      {!event.allDay ? `${dayjs.utc(event.start).format(`${timeFormat} `)}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', lineHeight: 1, fontWeight: 500, color: event.allDay ? '#fff' : 'inherit' }}>
                      {event.title}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2">{t('pages.companyCalendar.noData')}</Typography>
          )}
        </Box>
      </Popover>

      {selectedEventId ? (
        <EventPopover
          anchorEl={eventAnchorEl}
          onClose={onClosePopover}
          eventId={selectedEventId}
        />
      ) : null}
    </Fragment>
  )
}

export default DayEventsPopover;
